/* eslint-disable @next/next/no-img-element */
"use client";

import Chevron from "@/assets/svg/Chevron";
import { useEmblaCarousel } from "@frend-digital/ui/embla";
import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import { useEffectEvent } from "use-effect-event";
import {
  GalleryButton,
  GalleryDots,
  GallerySlide,
  GalleryThumb,
  GalleryThumbsButton,
  GalleryViewport,
} from "./MediaGalleryUI";
import styles from "./gallery.module.css";

type Media = {
  uri: string;
  alt: string;
};

interface Props {
  media?: Media[];
  booklet?: boolean;
}

const MediaGallery = ({ media: mediaList = [], booklet = false }: Props) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScroll, setCanScroll] = useState({
    nextThumb: true,
    prevThumb: true,
  });

  const disabled = mediaList.length === 1;
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({
    loop: true,
    speed: 30,
    active: true,
    align: "start",
  });
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
    loop: true,
    axis: "y",
    speed: 30,
    align: "start",
  });

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi.current || !emblaThumbsApi.current) return;
      emblaMainApi.current.scrollTo(index);
    },
    [emblaMainApi.current, emblaThumbsApi.current]
  );

  const scrollNextThumb = useCallback(() => {
    if (!emblaThumbsApi.current || !emblaMainApi.current) return;
    emblaThumbsApi.current.scrollNext();
  }, [emblaMainApi.current, emblaThumbsApi.current]);

  const scrollPrevThumb = useCallback(() => {
    if (!emblaThumbsApi.current || !emblaMainApi.current) return;
    emblaThumbsApi.current.scrollPrev();
  }, [emblaMainApi.current, emblaThumbsApi.current]);

  const scrollNext = useCallback(() => {
    if (!emblaThumbsApi.current || !emblaMainApi.current) return;
    emblaMainApi.current.canScrollNext() && emblaMainApi.current.scrollNext();
  }, [emblaMainApi.current, emblaThumbsApi.current]);

  const scrollPrev = useCallback(() => {
    if (!emblaThumbsApi.current || !emblaMainApi.current) return;
    emblaMainApi.current.canScrollPrev() && emblaMainApi.current.scrollPrev();
  }, [emblaMainApi.current, emblaThumbsApi.current]);

  const onSelect = useEffectEvent(() => {
    if (!emblaMainApi.current || !emblaThumbsApi.current) return;
    setSelectedIndex(emblaMainApi.current.selectedScrollSnap());
    emblaThumbsApi.current.scrollTo(emblaMainApi.current.selectedScrollSnap());

    setCanScroll((prev) => ({
      ...prev,
      nextThumb: emblaThumbsApi.current?.canScrollNext() || false,
      prevThumb: emblaThumbsApi.current?.canScrollPrev() || false,
    }));
  });

  useEffect(() => {
    if (!emblaMainApi.current) return;
    onSelect();
    emblaMainApi.current.on("select", onSelect);
    // emblaMainApi.current.on("reInit", onSelect);
  }, []);

  useEffect(() => {
    if (!emblaMainApi.current) return;
    if (disabled) {
      emblaMainApi.current.destroy();
    } else {
      emblaMainApi.current.reInit();
    }
  }, [mediaList, disabled]);

  const showNavButtons = mediaList.length > 3;
  return (
    <div
      className={clsx(
        styles.mediaGallery,
        disabled && styles.disabled,
        booklet && styles.booklet
      )}
    >
      <div className={styles.thumbs}>
        <GalleryThumbsButton
          disabled={!canScroll.prevThumb}
          onClick={scrollPrevThumb}
        >
          <Chevron direction={"up"} size="md" />
        </GalleryThumbsButton>
        <GalleryViewport ref={emblaThumbsRef}>
          <div className={styles.thumbsContainer}>
            {mediaList.map((media, index) => (
              <GalleryThumb
                onClick={() => onThumbClick(index)}
                selected={index === selectedIndex}
                index={index}
                alt={media.alt}
                imgSrc={media.uri}
                key={index}
              />
            ))}
          </div>
        </GalleryViewport>
        <GalleryThumbsButton
          disabled={!canScroll.nextThumb}
          onClick={scrollNextThumb}
        >
          <Chevron direction={"down"} size="md" />
        </GalleryThumbsButton>
      </div>

      <div className={styles.gallery}>
        {showNavButtons && (
          <GalleryButton className={styles.left} onClick={scrollPrev}>
            <Chevron direction={"left"} size="md" />
          </GalleryButton>
        )}
        <GalleryViewport ref={emblaMainRef}>
          <div className={styles.galleryContainer}>
            {mediaList.map((media, index) => (
              <GallerySlide priority={index === 0} media={media} key={index} />
            ))}
          </div>
        </GalleryViewport>
        <GalleryDots
          slides={mediaList}
          selectedIndex={selectedIndex}
          scrollTo={emblaMainApi.current?.scrollTo}
        />
        {showNavButtons && (
          <GalleryButton className={styles.right} onClick={scrollNext}>
            <Chevron direction={"right"} size="md" />
          </GalleryButton>
        )}
      </div>
    </div>
  );
};

export default MediaGallery;
